// src/pages/MeetSajeda.js

import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';

const useFadeInEffect = () => {
  const animatedElements = useRef([]);

  useEffect(() => {
    const elementsToObserve = animatedElements.current;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible', 'fade-in-premium');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -50px 0px' }
    );

    if (elementsToObserve) {
      elementsToObserve.forEach((el) => {
        if (el) observer.observe(el);
      });
    }
    return () => {
      if (elementsToObserve) {
        elementsToObserve.forEach((el) => {
          if (el) observer.unobserve(el);
        });
      }
    };
  }, []);

  const addToRefs = (el) => {
    if (el && !animatedElements.current.includes(el)) {
      animatedElements.current.push(el);
    }
  };
  return addToRefs;
};

const MeetSajeda = () => {
  const addToRefs = useFadeInEffect();
  const videoRef = useRef(null);
  const [isMuted, setIsMuted] = useState(true);

  useEffect(() => {
    const currentVideo = videoRef.current;
    const videoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (currentVideo) currentVideo.play().catch(e => console.log(e));
          } else {
            if (currentVideo) currentVideo.pause();
          }
        });
      },
      { threshold: 0.5 }
    );
    if (currentVideo) videoObserver.observe(currentVideo);
    return () => { if (currentVideo) videoObserver.unobserve(currentVideo); };
  }, []);

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(videoRef.current.muted);
    }
  };

  const phoneFrameStyle = {
    border: '12px solid #1a1a1a', borderRadius: '45px', overflow: 'hidden',
    width: '100%', maxWidth: '320px', margin: '0 auto',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.3)', aspectRatio: '9/16',
    position: 'relative', backgroundColor: '#000', transform: 'translateZ(0)'
  };

  return (
    <>
      <Helmet>
        <title>Meet Sajeda | Little Hibba</title>
        <meta name="description" content="I’m Sajeda, founder of Little Hibba. Discover my journey, work with me, and let's create meaningful stories together." />
      </Helmet>

      <style>{`
        .bg-premium-pattern { background-color: #f2f7fa; background-image: radial-gradient(#cfdbe6 1.5px, transparent 1.5px); background-size: 28px 28px; }
        .bg-premium-white { background-color: #ffffff; }
        .fade-in-premium { animation: fadeInPremium 1s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes fadeInPremium { from { opacity: 0; transform: translateY(40px); filter: blur(6px); } to { opacity: 1; transform: translateY(0); filter: blur(0); } }
        .invisible-start { opacity: 0; }
        .classy-heading { font-family: 'Playfair Display', serif; color: #1a365d; }
        .text-reading { font-size: 1.15rem; line-height: 1.8; color: #4a5568; }
        .gallery-img { border-radius: 16px; object-fit: cover; width: 100%; box-shadow: 0 10px 20px rgba(0,0,0,0.08); transition: transform 0.4s ease, box-shadow 0.4s ease; }
        .gallery-img:hover { transform: scale(1.03) translateY(-5px); box-shadow: 0 15px 30px rgba(0,0,0,0.12); }
        .workshop-card { border: none; border-radius: 20px; background: #ffffff; box-shadow: 0 10px 40px rgba(0,0,0,0.05); transition: all 0.4s ease; position: relative; overflow: hidden; }
        .workshop-card:hover { transform: translateY(-8px); box-shadow: 0 20px 50px rgba(0,0,0,0.1); }
        .badge-classy { background-color: #e2eff5; color: #1a365d; font-weight: 600; letter-spacing: 1px; padding: 8px 18px; border-radius: 50px; }
        .price-tag { font-family: 'Playfair Display', serif; color: #d4af37; }
        .hero-section { padding-top: max(120px, 12vh); padding-bottom: 80px; }
        .unmute-btn { position: absolute; bottom: 20px; right: 20px; background: rgba(0,0,0,0.6); color: white; border: none; border-radius: 50%; width: 45px; height: 45px; display: flex; align-items: center; justify-content: center; cursor: pointer; backdrop-filter: blur(4px); transition: background 0.3s; z-index: 10; }
        .unmute-btn:hover { background: rgba(0,0,0,0.8); }
      `}</style>

      {/* --- SECTION 1: HOME (HERO) --- */}
      <section className="hero-section bg-premium-pattern">
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="pe-lg-5 invisible-start mb-5 mb-lg-0" ref={addToRefs}>
              <span className="badge-classy text-uppercase mb-3 d-inline-block">Home</span>
              <h1 className="display-3 classy-heading fw-bold mb-4">Hello, I’m Sajeda.</h1>
              <div className="text-reading">
                <p>Many call me Hibba, my daughter’s name, meaning "gift from Allah.” This feels fitting because we see every child, story, and book as a treasured gift, with their learning as an amanah entrusted to us by Allah swt.</p>
                <p>I’ve always loved stories and how they’re shared and created, from picture books and movies to poetry and, of course, the Quran. The first word revealed to Prophet Muhammad swt was Iqra (Read). Reading is central to our faith and something all of us—mothers, fathers, and children—should enjoy and invest in.</p>
                <p>I never planned to work in publishing, but my passion for creativity has always stayed with me. After my second child was born, having worked in banking and lived with my in-laws as a young mother of two, I decided to create stories to reconnect with the old me and the things I loved—just for myself.</p>
                <p>Life has brought many joys and challenges, pregnancies, raising children, blessings, and lessons, but something kept drawing me toward this path. Guided by Allah, I’ve explored different routes in publishing. I want to share my experiences and knowledge, build friendships worldwide, and foster a community based on honesty and trust.</p>
                <p className="fw-bold text-dark mt-4 border-start border-4 border-warning ps-3 py-2">
                  My goal is to reveal the truth about the industry, something others won’t…
                </p>
              </div>
            </Col>
            <Col lg={6} className="text-center invisible-start" ref={addToRefs}>
              <img src="/images/sajeda/12.jpg" alt="Sajeda Founder of Little Hibba" className="img-fluid rounded-5 shadow-lg" style={{ maxHeight: '75vh', objectFit: 'cover' }} />
            </Col>
          </Row>
        </Container>
      </section>

      {/* --- SECTION 2: WORK WITH ME --- */}
      <section className="py-5 bg-premium-white">
        <Container className="py-lg-5">
          <div className="text-center mb-5 pb-3 invisible-start" ref={addToRefs}>
            <span className="badge-classy text-uppercase mb-3 d-inline-block">Work With Me</span>
            <h2 className="classy-heading display-4">Sharing all the parts<br/>that many don't tell you about…</h2>
            <p className="text-muted mt-4 mx-auto text-reading" style={{ maxWidth: '800px' }}>
              While I’m still learning, I feel confident enough to share what I’ve learned to help Muslim creators better understand the industry. Join me in these intimate workshops.
            </p>
          </div>

          {/* WORKSHOP 1 SUMMARY */}
          <Card className="workshop-card mb-5 invisible-start p-4 p-md-5 border-0 bg-light" ref={addToRefs}>
            <Row className="align-items-center flex-md-row-reverse">
              <Col md={5} className="mb-4 mb-md-0 text-center">
                <img src="/images/sajeda/4.jpg" alt="Zoom Meeting Workshop" className="img-fluid rounded-4 shadow-sm" style={{ maxHeight: '350px' }} />
              </Col>
              <Col md={7} className="pe-md-5">
                <h6 className="text-primary fw-bold text-uppercase tracking-wider mb-2">Workshop 1</h6>
                <h3 className="classy-heading mb-4">What You Need to Know Before You Start Your Children’s Book</h3>
                <p className="text-reading mb-4">Unsure if your Muslim kids’ book idea will actually work? Get clear on formats, markets and what publishers expect – before you write a word.</p>
                <div className="d-flex align-items-center justify-content-between pt-3 border-top">
                  <span className="display-6 fw-bold price-tag mb-0">£189</span>
                  <Link to="/workshop-1">
                    <Button variant="primary" size="lg" className="rounded-pill px-5 fw-bold shadow-sm">Find Out More</Button>
                  </Link>
                </div>
              </Col>
            </Row>
          </Card>

          {/* WORKSHOP 2 SUMMARY */}
          <Card className="workshop-card mb-5 invisible-start p-4 p-md-5 border-0 bg-light" ref={addToRefs}>
            <Row className="align-items-center">
              <Col md={5} className="mb-4 mb-md-0 text-center">
                <img src="/images/sajeda/11.jpg" alt="Marketing and Production" className="img-fluid rounded-4 shadow-sm" style={{ maxHeight: '350px' }} />
              </Col>
              <Col md={7} className="ps-md-5">
                <h6 className="text-primary fw-bold text-uppercase tracking-wider mb-2">Workshop 2</h6>
                <h3 className="classy-heading mb-4">A Publisher’s Mainstream Marketing Steps – Copy Them for Your Book</h3>
                <p className="text-reading mb-4">Big publishers don’t guess at launches. Copy their proven campaign steps, but make them halal and realistic for your Muslim book.</p>
                <div className="d-flex align-items-center justify-content-between pt-3 border-top">
                  <span className="display-6 fw-bold price-tag mb-0">£189</span>
                  <Link to="/workshop-2">
                    <Button variant="primary" size="lg" className="rounded-pill px-5 fw-bold shadow-sm">Find Out More</Button>
                  </Link>
                </div>
              </Col>
            </Row>
          </Card>

          {/* WORKSHOP 3 SUMMARY */}
          <Card className="workshop-card mb-5 invisible-start p-4 p-md-5 border-0 bg-light" ref={addToRefs}>
            <Row className="align-items-center flex-md-row-reverse">
              <Col md={5} className="mb-4 mb-md-0 text-center">
                <img src="/images/sajeda/9.jpg" alt="Roles in Publishing" className="img-fluid rounded-4 shadow-sm" style={{ maxHeight: '350px' }} />
              </Col>
              <Col md={7} className="pe-md-5">
                <h6 className="text-primary fw-bold text-uppercase tracking-wider mb-2">Workshop 3</h6>
                <h3 className="classy-heading mb-4">Roles You Can Do in Children’s Publishing</h3>
                <p className="text-reading mb-4">Children’s publishing isn’t just about authors and illustrators. Discover where your skills fit – editorial, design, sales, digital.</p>
                <div className="d-flex align-items-center justify-content-between pt-3 border-top">
                  <span className="display-6 fw-bold price-tag mb-0">£189</span>
                  <Link to="/workshop-3">
                    <Button variant="primary" size="lg" className="rounded-pill px-5 fw-bold shadow-sm">Find Out More</Button>
                  </Link>
                </div>
              </Col>
            </Row>
          </Card>
        </Container>
      </section>

      {/* --- SECTION 3: THE JOURNEY --- */}
      <section className="py-5 bg-premium-pattern">
        <Container className="py-lg-5">
          <div className="text-center mb-5 invisible-start" ref={addToRefs}>
            <h2 className="classy-heading display-4">The Journey</h2>
            <div style={{ width: '80px', height: '4px', backgroundColor: '#d4af37', margin: '20px auto', borderRadius: '2px' }}></div>
          </div>
          
          <Row className="align-items-center">
            <Col lg={5} className="mb-5 mb-lg-0 invisible-start" ref={addToRefs}>
              <div style={phoneFrameStyle}>
                <video ref={videoRef} className="w-100 h-100" loop playsInline muted={isMuted} style={{ objectFit: 'cover' }}>
                  <source src="/videos/reel - intro.mp4" type="video/mp4" />
                </video>
                <button onClick={toggleMute} className="unmute-btn" aria-label="Toggle sound">
                  <i className={`bi ${isMuted ? 'bi-volume-mute-fill' : 'bi-volume-up-fill'} fs-4`}></i>
                </button>
              </div>
            </Col>

            <Col lg={7} className="invisible-start text-reading ps-lg-5" ref={addToRefs}>
              <p>I remember when I first had Hibba, I was focused on growing as a mother and went out to buy a bunch of Muslim children's books. They were text-heavy and a bit hard to enjoy for kids, with long, complex hadiths that didn’t quite match the lively books I grew up reading at the library. It felt like Muslim publishing was stuck in the past. So, I thought, why not create something for my own kids?</p>
              <p>I’ve always loved personalised things... When I was writing stories for Hibba, I made her the main character. Back then, we still had physical photos, so I would cut out her picture and include it in the illustrations. Now, she would hear her name, see herself in the pictures, and listen to a story that felt familiar and close to her world. And that’s how it all began…</p>
              <p>It started with a simple idea: personalised children’s books where Muslim kids could see themselves as the hero of their own Islamic story. I spent hours learning Photoshop, cropping photos, making everything fit with the illustrations, editing lines for perfect personalisation, figuring out print-on-demand platforms, and crying over the costs.</p>
              <p>From that hands-on experience, I learned a lot about children’s publishing: which formats work best, how to produce stories at scale, what parents look for during Eid or birthdays. I discovered what sells: names, familiarity, joy, and what doesn’t: over-complicated plots and boring text.</p>
              <p>Today, we publish full picture books and early readers with universal themes, but grounded in Muslim values—stories where faith isn’t an afterthought, but the very heartbeat of the story.</p>
            </Col>
          </Row>

          <Row className="mt-5 pt-4 g-4 invisible-start" ref={addToRefs}>
            <Col md={4}><img src="/images/sajeda/5.jpg" alt="Working on illustrations" className="gallery-img h-100" /></Col>
            <Col md={4}><img src="/images/sajeda/6.jpg" alt="Brainstorming ideas" className="gallery-img h-100" /></Col>
            <Col md={4}><img src="/images/sajeda/7.jpg" alt="Reading to children" className="gallery-img h-100" /></Col>
          </Row>
        </Container>
      </section>

      {/* --- SECTION 4: VISUAL BREAK / GALLERY --- */}
      <section className="py-5 bg-premium-white">
        <Container className="py-lg-5">
          <div className="text-center mb-5 invisible-start" ref={addToRefs}>
            <h3 className="classy-heading display-6">A Glimpse into the World</h3>
            <div style={{ width: '50px', height: '3px', backgroundColor: '#d4af37', margin: '15px auto', borderRadius: '2px' }}></div>
          </div>
          <Row className="g-3 justify-content-center align-items-stretch invisible-start" ref={addToRefs}>
            <Col xs={6} md={3}><img src="/images/sajeda/1.jpg" alt="Bookshop Sketch" className="gallery-img h-100" /></Col>
            <Col xs={6} md={2}><img src="/images/sajeda/2.jpg" alt="Gallery item" className="gallery-img h-100" /></Col>
            <Col xs={6} md={2}><img src="/images/sajeda/3.jpg" alt="Gallery item" className="gallery-img h-100" /></Col>
            <Col xs={6} md={3}><img src="/images/sajeda/8.jpg" alt="Bookshop Colored" className="gallery-img h-100" /></Col>
            <Col xs={12} md={2}><img src="/images/sajeda/10.jpg" alt="Sajeda selfie" className="gallery-img h-100" style={{ maxHeight: '250px' }} /></Col>
          </Row>
        </Container>
      </section>

      {/* --- SECTION 5: CONTACT US (CTA) --- */}
      <section className="py-5" style={{ backgroundColor: '#1a365d' }}>
        <Container className="text-center text-white invisible-start py-5 my-lg-4" ref={addToRefs}>
          <h2 className="classy-heading text-white display-4 mb-4">Ready to Write Your Story?</h2>
          <p className="lead mb-5 mx-auto" style={{ maxWidth: '700px', opacity: '0.9', lineHeight: '1.8' }}>
            Whether you want to publish your first book or find your place in the children's publishing industry, I am here to guide you. Secure your spot today!
          </p>
          <Link to="/contact">
            <Button variant="light" size="lg" className="px-5 py-3 rounded-pill fw-bold shadow-lg text-primary text-uppercase" style={{ letterSpacing: '2px' }}>
              Contact Us
            </Button>
          </Link>
        </Container>
      </section>
    </>
  );
};

export default MeetSajeda;